import React, { useState, useMemo } from 'react';
import { X, Search, LayoutTemplate, Loader2 } from 'lucide-react';
import { useGetTemplatesQuery } from '../../features/templates/templatesApi';
import { DEFAULT_PLACEHOLDER } from '../../utils/assetUtils';

interface TemplatesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: any) => void;
}

export function TemplatesPanel({ isOpen, onClose, onSelectTemplate }: TemplatesPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Simple debounce for search
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: response, isLoading, isError } = useGetTemplatesQuery({
    search: debouncedSearch
  });

  const templates = response?.data || [];

  // Group templates by category
  const categories = useMemo(() => {
    const groups: Record<string, any[]> = {};

    templates.forEach(t => {
      const cat = t.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push({
        id: t._id,
        name: t.name,
        previewUrl: t.thumbnail,
        // Pass the full raw API data so the editor can use it
        fullData: t.data
      });
    });

    return Object.entries(groups).map(([name, items]) => ({
      id: name.toLowerCase().replace(/\s+/g, '-'),
      name,
      templates: items
    }));
  }, [templates]);

  const handleDragStart = (e: React.DragEvent, template: any) => {
    e.dataTransfer.setData('template-data', JSON.stringify(template));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <>


      <div
        className={`fixed left-[98px] top-[80px] w-[340px] h-[calc(100vh-100px)] bg-white rounded-2xl shadow-xl z-40 flex flex-col transition-opacity duration-300 ease-in-out border border-gray-100 overflow-hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100 bg-white z-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-gray-900 text-lg">Templates</h2>
              <p className="text-xs text-gray-500 mt-0.5">Choose a design to start with</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
              aria-label="Close panel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-transparent focus:bg-white focus:border-[#0ba29b]/30 focus:ring-2 focus:ring-[#0ba29b]/10 rounded-lg text-sm outline-none transition-all placeholder:text-gray-400"
            />
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 custom-scrollbar">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mb-3 text-[#0ba29b]" />
              <p className="text-sm font-medium">Loading templates...</p>
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center h-full text-red-400 py-12 text-center">
              <LayoutTemplate size={32} className="mb-3 opacity-20" />
              <p className="text-sm font-medium">Failed to load templates</p>
              <p className="text-xs mt-1">Please try again later</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12 text-center">
              <LayoutTemplate size={32} className="mb-3 opacity-20" />
              <p className="text-sm font-medium">No templates found</p>
              <p className="text-xs mt-1">Try searching for something else</p>
            </div>
          ) : (
            <div className="space-y-8 pt-6">
              {categories.map(category => (
                <div key={category.id}>
                  <h3 className="font-medium text-gray-900 text-sm mb-3 flex items-center gap-2">
                    {category.name}
                    <span className="text-xs font-normal text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                      {category.templates.length}
                    </span>
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {category.templates.map(template => (
                      <div
                        key={template.id}
                        className="group relative aspect-[3/2] rounded-lg overflow-hidden cursor-pointer border border-gray-200 hover:border-[#0ba29b] hover:shadow-md transition-all bg-gray-100"
                        draggable
                        onDragStart={(e) => handleDragStart(e, template)}
                        onClick={() => onSelectTemplate(template)}
                      >
                        <img
                          src={template.previewUrl || DEFAULT_PLACEHOLDER}
                          alt={template.name}
                          className={`w-full h-full transition-transform duration-500 group-hover:scale-105 ${template.previewUrl ? 'object-cover' : 'object-contain p-4 opacity-10'}`}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = DEFAULT_PLACEHOLDER;
                            target.className = "w-full h-full object-contain p-4 opacity-10";
                            const label = target.parentElement?.querySelector('.no-preview-label');
                            if (label) label.classList.remove('hidden');
                          }}
                        />
                        {!template.previewUrl && (
                          <div className="no-preview-label absolute bottom-2 inset-x-0 text-center text-[8px] text-gray-400 font-bold uppercase tracking-widest leading-none px-1">
                            No preview available
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                          <p className="text-white text-xs font-medium truncate">{template.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

